import { Fragment } from 'react';

import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityLabelItem,
  ActivityTagItem,
  diffItems,
} from '@spotify/plugin-confidence-activity-react';

import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface Treatment {
  name?: string;
  weight: number;
  variant?: {
    name: string;
  };
  displayName?: string;
  description?: string;
  imageUri?: string;
}

interface TreatmentsData {
  treatments?: Treatment[];
}

const getTreatmentKey = (t: Treatment) => t.displayName || t.name || '';

const pluralizeTreatment = (count: number) =>
  count === 1 ? 'treatment' : 'treatments';

const TreatmentChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as TreatmentsData | null;
  const newValue = change.newValue as TreatmentsData | null;
  const oldTreatments = oldValue?.treatments ?? [];
  const newTreatments = newValue?.treatments ?? [];

  const { added: addedTreatments, removed: removedTreatments } = diffItems(
    oldTreatments,
    newTreatments,
    getTreatmentKey,
  );

  const weightChanges = newTreatments
    .map(newT => {
      const oldT = oldTreatments.find(
        t => (t.displayName || t.name) === (newT.displayName || newT.name),
      );

      if (oldT && oldT.weight !== newT.weight) {
        return {
          name: newT.displayName || newT.name || '',
          oldWeight: oldT.weight,
          newWeight: newT.weight,
        };
      }
      return null;
    })
    .filter(Boolean);

  // Skip rendering when titleElement shows single-item details
  if (
    (addedTreatments.length === 1 && removedTreatments.length === 0) ||
    (removedTreatments.length === 1 && addedTreatments.length === 0)
  ) {
    return null;
  }

  return (
    <Fragment>
      {addedTreatments.length > 0 && (
        <ActivityItem.Section
          label={`Added ${pluralizeTreatment(addedTreatments.length)}`}
        >
          {addedTreatments.map((treatment, index) => (
            <div key={index}>
              <ActivityTagItem
                tagLabel={treatment.displayName || treatment.name || ''}
                newValue={`(${treatment.weight / 100}%)`}
                ariaLabel="Treatment name"
              />
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {removedTreatments.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${pluralizeTreatment(removedTreatments.length)}`}
        >
          {removedTreatments.map((treatment, index) => (
            <div key={index}>
              <ActivityTagItem
                tagLabel={treatment.displayName || treatment.name || ''}
                ariaLabel="Treatment name"
              />
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {weightChanges.length > 0 && (
        <ActivityItem.Section
          label={`Updated ${pluralizeTreatment(weightChanges.length)} weight`}
        >
          {weightChanges.map((newChange, index) => (
            <div key={index}>
              <ActivityTagItem
                tagLabel={newChange!.name}
                oldValue={`${newChange!.oldWeight / 100}%`}
                newValue={`${newChange!.newWeight / 100}%`}
                ariaLabel="Treatment name"
              />
            </div>
          ))}
        </ActivityItem.Section>
      )}
    </Fragment>
  );
};

export interface CreateTreatmentsChangeExtensionOptions {
  id: string;
  fieldPath: string;
}

export const createTreatmentsChangeExtension = (
  options: CreateTreatmentsChangeExtensionOptions,
) => {
  return createWorkflowUpdatesChangeExtension({
    id: options.id,
    predicate: ({ change }) => change.fieldPath === options.fieldPath,
    title: change => {
      const oldValue = change.oldValue as TreatmentsData | null;
      const newValue = change.newValue as TreatmentsData | null;
      const oldTreatments = oldValue?.treatments ?? [];
      const newTreatments = newValue?.treatments ?? [];

      const { added, removed } = diffItems(
        oldTreatments,
        newTreatments,
        getTreatmentKey,
      );

      if (added.length === 1 && removed.length === 0) {
        return `Added treatment ${getTreatmentKey(added[0])}`;
      }

      if (removed.length === 1 && added.length === 0) {
        return `Removed treatment ${getTreatmentKey(removed[0])}`;
      }

      if (newTreatments.length > 0) {
        return `Updated ${pluralizeTreatment(newTreatments.length)}`;
      }

      return `Updated treatment options`;
    },
    titleElement: change => {
      const oldValue = change.oldValue as TreatmentsData | null;
      const newValue = change.newValue as TreatmentsData | null;
      const oldTreatments = oldValue?.treatments ?? [];
      const newTreatments = newValue?.treatments ?? [];

      const { added, removed } = diffItems(
        oldTreatments,
        newTreatments,
        getTreatmentKey,
      );

      if (added.length === 1 && removed.length === 0) {
        return (
          <ActivityLabelItem
            text="Added treatment"
            tagLabel={getTreatmentKey(added[0])}
            ariaLabel="Treatment name"
          />
        );
      }

      if (removed.length === 1 && added.length === 0) {
        return (
          <ActivityLabelItem
            text="Removed treatment"
            tagLabel={getTreatmentKey(removed[0])}
            ariaLabel="Treatment name"
          />
        );
      }

      // Check for weight changes when no treatments were added/removed
      if (added.length === 0 && removed.length === 0) {
        const changedTreatments = newTreatments.filter(newT => {
          const oldT = oldTreatments.find(
            t => (t.displayName || t.name) === (newT.displayName || newT.name),
          );
          return oldT && oldT.weight !== newT.weight;
        });

        if (changedTreatments.length === 1) {
          return (
            <ActivityLabelItem
              text="Updated"
              tagLabel={getTreatmentKey(changedTreatments[0])}
              ariaLabel="Treatment name"
            />
          );
        }
      }

      return null;
    },
    render: TreatmentChangeRender,
  });
};
